import {
  Alert,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import type { WorkflowFieldDefinition } from '../model';
import {
  ChoiceControl,
  FormSection,
  FormTextField,
  ValueBadge,
} from '../../../design-system';
import {
  fieldOptions,
  workflowFieldValidationError,
  workflowValidationMaximumLength,
  workflowValidationNumber,
} from '../models/workflowRequestPageModel';

export function DynamicField({ field, value, onChange, disabled = false }: { field: WorkflowFieldDefinition; value: unknown; onChange: (value: unknown) => void; disabled?: boolean }) {
  const validationError = workflowFieldValidationError(field, value);
  if (field.type === 'DISPLAY_TEXT') return <Alert severity="info">{field.label}</Alert>;
  if (field.type === 'DATE_RANGE') {
    const range = value && typeof value === 'object' && !Array.isArray(value) ? value as { from?: string; to?: string } : {};
    return <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <FormTextField fullWidth type="date" label={`${field.label} 시작`} value={range.from ?? ''} onChange={(event) => onChange({ ...range, from: event.target.value })} required={field.required} disabled={disabled} error={Boolean(validationError)} />
      <FormTextField fullWidth type="date" label={`${field.label} 종료`} value={range.to ?? ''} onChange={(event) => onChange({ ...range, to: event.target.value })} required={field.required} disabled={disabled} error={Boolean(validationError)} helperText={validationError ?? field.helpText} />
    </Stack>;
  }
  const options = fieldOptions(field);
  if (field.type === 'SINGLE_SELECT') return <FormTextField select label={field.label} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={disabled} error={Boolean(validationError)} helperText={validationError ?? field.helpText}>{!field.required && <MenuItem value="">선택 안 함</MenuItem>}{options.map((option) => <MenuItem key={option.value} value={option.value}><ValueBadge label={option.label ?? option.value} /></MenuItem>)}</FormTextField>;
  if (field.type === 'MULTI_SELECT') {
    const selected = Array.isArray(value) ? value.map(String) : [];
    return <FormSection heading={`${field.label}${field.required ? ' *' : ''}`}>
      <Stack>
        {options.map((option) => <ChoiceControl key={option.value} kind="checkbox" checked={selected.includes(option.value)} disabled={disabled} onCheckedChange={(checked) => onChange(checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))} label={option.label ?? option.value} />)}
        {!options.length && <Typography variant="caption" color="text.secondary">설정된 선택 항목이 없습니다.</Typography>}
        {(validationError || field.helpText) && <Typography variant="caption" color={validationError ? 'error' : 'text.secondary'}>{validationError ?? field.helpText}</Typography>}
      </Stack>
    </FormSection>;
  }
  if (field.type === 'FILE') return <FormTextField disabled label={field.label} value="" placeholder="파일을 끌어다 놓거나 선택" helperText={`파일을 최대 ${Number(field.config?.maxFiles ?? 10)}개까지 등록할 수 있습니다.`} />;
  if (field.type === 'WORKFLOW_REFERENCE') return <FormTextField disabled label={field.label} value="" placeholder="다른 워크플로우 문서 검색" helperText={field.helpText} />;
  if (field.type === 'PERSON' || field.type === 'ORGANIZATION') return <FormTextField disabled label={field.label} value="" placeholder="목록에서 선택" helperText={field.helpText} />;
  const numberField = field.type === 'NUMBER' || field.type === 'CURRENCY';
  const minimum = workflowValidationNumber(field.validation?.min);
  const maximum = workflowValidationNumber(field.validation?.max);
  const maximumLength = workflowValidationMaximumLength(field.validation?.maxLength);
  const helperText = validationError ?? field.helpText;
  return <FormTextField
    label={field.label}
    value={value ?? ''}
    onChange={(event) => onChange(numberField ? event.target.value === '' ? '' : Number(event.target.value) : event.target.value)}
    required={field.required}
    disabled={disabled}
    error={Boolean(validationError)}
    helperText={helperText}
    type={field.type === 'DATE' ? 'date' : numberField ? 'number' : 'text'}
    multiline={field.type === 'LONG_TEXT'}
    minRows={field.type === 'LONG_TEXT' ? 4 : undefined}
    slotProps={numberField ? { htmlInput: { step: 'any', min: minimum, max: maximum } } : maximumLength !== undefined ? { htmlInput: { maxLength: maximumLength } } : undefined}
  />;
}
